'use client'

import dynamic from 'next/dynamic'

import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

const CobeGlobe = dynamic(
  () => import('@/components/aceternity/cobe-globe').then((m) => m.CobeGlobe),
  {
    ssr: false,
  }
)

export function CTASection() {
  return (
    <section className="bg-background text-foreground relative py-32 pb-0">
      {/* Background decoration — subtle 96px grid (opacity baked into the tile) */}
      <div className="absolute inset-0 bg-grid-pattern" />

      <div className="container flex flex-col items-center justify-center gap-4 relative z-10">
        {/* Eyebrow — borderless, subtle fill */}
        <div className="inline-flex items-center px-4 py-2 rounded-full backdrop-blur-md bg-muted/30">
          <p className="text-muted-foreground text-xs sm:text-sm md:text-base font-medium">
            Empowering Organizations Worldwide
          </p>
        </div>

        {/* Title — h2, not h1. This CTA is appended to nearly every page, so an
            h1 here gave each page a second, identical top-level heading that
            competed with its real subject. */}
        <h2
          className="font-heading w-full max-w-5xl text-center font-normal leading-tight
            text-[2.3rem] sm:text-[2.8rem] md:text-5xl lg:text-7xl"
        >
          <ChromaText>
            <span className="text-foreground">Transform Ideas </span>
            <span className="bg-clip-text text-transparent bg-emerald-600 dark:bg-primary-active">
              Into Impact
            </span>
          </ChromaText>
        </h2>

        {/* Description */}
        <p className="max-w-2xl text-center text-sm sm:text-base md:text-lg text-muted-foreground/80 mt-2 px-4">
          Learn how our expertise can unlock new opportunities and drive your
          organization's success
        </p>

        {/* Button */}
        <Button asChild size="xl" className="relative z-10 mt-4">
          <a href="/schedule-meeting" target="_blank" rel="noopener noreferrer">
            Book an Appointment
          </a>
        </Button>
      </div>

      {/* Globe — anchored to the TOP so the whole top of the sphere is always
          visible (never cropped), while it is tall enough that its bottom
          ~25% runs past the container and hides into the footer. */}
      <div className="relative w-full overflow-hidden mt-6 md:mt-8 h-[300px] sm:h-[380px] md:h-[460px] lg:h-[540px]">
        {/* Ambient mesh glow behind the globe — same language as the hero's
            MeshGlow: blurred diagonal mint/sky beams, solid toward the
            footer seam, dissolving upward. IDs are prefixed so they never
            collide with the hero's gradients on the same page. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
        >
          <svg
            viewBox="0 0 1440 636"
            preserveAspectRatio="none"
            className="absolute inset-x-0 bottom-0 h-full w-full opacity-70 dark:opacity-30"
          >
            <defs>
              {/* True mesh: soft radial falloffs per blob; overlapping hues
                  blend into one organic field (no repeating shapes). */}
              <radialGradient id="cta-mesh-mint">
                <stop offset="0" stopColor="#37ecb0" stopOpacity="0.5" />
                <stop offset="0.55" stopColor="#37ecb0" stopOpacity="0.16" />
                <stop offset="1" stopColor="#37ecb0" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="cta-mesh-sky">
                <stop offset="0" stopColor="#0ea5e9" stopOpacity="0.42" />
                <stop offset="0.55" stopColor="#0ea5e9" stopOpacity="0.14" />
                <stop offset="1" stopColor="#0ea5e9" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="cta-mesh-teal">
                <stop offset="0" stopColor="#10b981" stopOpacity="0.36" />
                <stop offset="0.55" stopColor="#10b981" stopOpacity="0.12" />
                <stop offset="1" stopColor="#10b981" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="cta-mesh-band" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#37ecb0" stopOpacity="0" />
                <stop offset="0.55" stopColor="#37ecb0" stopOpacity="0.2" />
                <stop
                  offset="1"
                  stopColor="hsl(var(--background))"
                  stopOpacity="0.9"
                />
              </linearGradient>
              <filter
                id="cta-mesh-blur"
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
              >
                <feGaussianBlur stdDeviation="55" />
              </filter>
            </defs>
            <g filter="url(#cta-mesh-blur)">
              {/* Clustered toward the footer seam, dissolving upward —
                  irregular positions/sizes/rotations, nothing tiles */}
              <ellipse
                cx="230"
                cy="590"
                rx="450"
                ry="265"
                transform="rotate(-8 230 590)"
                fill="url(#cta-mesh-mint)"
              />
              <ellipse
                cx="690"
                cy="660"
                rx="520"
                ry="300"
                fill="url(#cta-mesh-sky)"
              />
              <ellipse
                cx="1060"
                cy="560"
                rx="400"
                ry="250"
                transform="rotate(10 1060 560)"
                fill="url(#cta-mesh-teal)"
              />
              <ellipse
                cx="1390"
                cy="650"
                rx="410"
                ry="245"
                transform="rotate(-12 1390 650)"
                fill="url(#cta-mesh-mint)"
              />
              <ellipse
                cx="480"
                cy="470"
                rx="300"
                ry="190"
                transform="rotate(14 480 470)"
                fill="url(#cta-mesh-sky)"
              />
            </g>
            <rect
              x="0"
              y="0"
              width="1440"
              height="636"
              fill="url(#cta-mesh-band)"
            />
          </svg>
        </div>

        <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[135%] aspect-square max-w-none">
          <CobeGlobe />
        </div>

        {/* Smooth gradient blend where the globe's base meets the footer */}
        <div
          className="absolute bottom-0 left-0 right-0 h-40 md:h-52 pointer-events-none z-20"
          style={{
            background: `linear-gradient(to top,
              hsl(var(--background)) 0%,
              hsl(var(--background) / 0.95) 15%,
              hsl(var(--background) / 0.85) 30%,
              hsl(var(--background) / 0.65) 50%,
              hsl(var(--background) / 0.4) 70%,
              hsl(var(--background) / 0.15) 85%,
              transparent 100%
            )`,
          }}
        />
      </div>
    </section>
  )
}
